import MuiTableContainer from '@material-ui/core/TableContainer';
import MuiTableRow from '@material-ui/core/TableRow';
import React, { ReactNode, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { TextBlock } from '../TextBlock';
import { BaseTable, SharedBaseTableProps } from './BaseTable';
import { StyledTableCell } from './TableCell';
import { TableRow, TableRowHtmlProps } from './TableRow';
import { BaseTableRow } from './types';

// Don't show focus outline on table click, but do show it when keyboard tabbed to the table
export const StyledTableContainer = styled(MuiTableContainer)`
  :focus:not(:focus-visible) {
    outline: none;
  }
`;

// Full-bleed cell so dummy-row content isn't indented by the default
// first-column padding or split by a cell border.
const DummyRowCell = styled(StyledTableCell)`
  && {
    padding: 0;
    border-bottom: none;
  }
`;

function DummyRow({
  children,
  colSpan,
}: {
  children: ReactNode;
  colSpan: number;
}) {
  return (
    <MuiTableRow>
      <DummyRowCell colSpan={colSpan}>{children}</DummyRowCell>
    </MuiTableRow>
  );
}
type CommonTableProps<RowEntity extends BaseTableRow> =
  SharedBaseTableProps<RowEntity> & {
    /** Full-width content rendered as the first body row, below the header. */
    dummyRow?: ReactNode;
    className?: string;
    emptyTablePrimaryText?: string;
    emptyTableSecondaryText?: string;
    getRowProps?: (row: RowEntity) => TableRowHtmlProps;
    isPrefilteredAndNotEmpty?: boolean;
    makeTableFocusable?: boolean;
    resultsNotFoundPrimaryText?: string;
    resultsNotFoundSecondaryText?: string;
    rowFilter?: (row: RowEntity) => boolean;
    rows: RowEntity[];
    scrollToBottomOnRowAdded?: boolean;
  };

type SelectProps<RowEntity extends BaseTableRow> = {
  getCheckboxAriaLabel: (row: RowEntity) => string;
  getIsRowSelected: (row: RowEntity) => boolean;
  isRowSelectionDisabled?: boolean;
  onToggleRow: (row: RowEntity, isChecked: boolean) => void;
};

type RegularTableProps<RowEntity extends BaseTableRow> =
  CommonTableProps<RowEntity> & {
    canRowsBeSelected?: false;
  } & Partial<SelectProps<RowEntity>>;

type TableWithSelectProps<RowEntity extends BaseTableRow> =
  CommonTableProps<RowEntity> & {
    canRowsBeSelected: true;
  } & SelectProps<RowEntity>;

export type TableProps<RowEntity extends BaseTableRow> =
  | RegularTableProps<RowEntity>
  | TableWithSelectProps<RowEntity>;

export function Table<RowEntity extends BaseTableRow>({
  ariaLabel,
  dummyRow,
  canRowsBeSelected = false,
  className,
  columns,
  emptyTablePrimaryText = 'No items to show.',
  emptyTableSecondaryText = '',
  getCheckboxAriaLabel,
  getIsRowSelected,
  getRowProps,
  isCompact,
  isPrefilteredAndNotEmpty = false,
  isRowSelectionDisabled = false,
  makeTableFocusable = false,
  onSortByColumn,
  onToggleRow,
  resultsNotFoundPrimaryText = 'No results found.',
  resultsNotFoundSecondaryText = 'We couldn’t find what you are looking for. Try searching again using different keywords.',
  rowFilter,
  rows,
  scrollToBottomOnRowAdded = false,
  sortState,
}: TableProps<RowEntity>) {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const prevRowCountRef = useRef(rows.length);

  useEffect(() => {
    if (!scrollToBottomOnRowAdded) return;

    const currentRowCount = rows.length;
    if (
      currentRowCount > prevRowCountRef.current &&
      tableContainerRef.current
    ) {
      tableContainerRef.current.scrollTo({
        top: tableContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
    prevRowCountRef.current = currentRowCount;
  }, [rows.length, scrollToBottomOnRowAdded]);

  const rowsWithShouldHide = useMemo(
    () =>
      rows.map((row) => ({
        row,
        shouldHide: !!rowFilter && !rowFilter(row),
      })),
    [rowFilter, rows],
  );

  const isHidingAllRows = useMemo(
    () =>
      rowsWithShouldHide.filter((row) => row.shouldHide).length === rows.length,
    [rows.length, rowsWithShouldHide],
  );

  const hasNoRows = !rows.length;

  return (
    <StyledTableContainer
      ref={tableContainerRef}
      className={className}
      tabIndex={makeTableFocusable ? 0 : -1}
    >
      <BaseTable
        ariaLabel={ariaLabel}
        canRowsBeSelected={canRowsBeSelected}
        columns={columns}
        isCompact={isCompact}
        onSortByColumn={onSortByColumn}
        sortState={sortState}
      >
        {dummyRow != null && (
          <DummyRow colSpan={columns.length + (canRowsBeSelected ? 1 : 0)}>
            {dummyRow}
          </DummyRow>
        )}
        {rowsWithShouldHide.map(({ row, shouldHide }) => (
          <TableRow<RowEntity>
            key={row.id}
            canRowsBeSelected={canRowsBeSelected}
            columns={columns}
            getCheckboxAriaLabel={getCheckboxAriaLabel}
            getIsRowSelected={getIsRowSelected}
            getRowProps={getRowProps}
            isCompact={isCompact}
            isRowSelectionDisabled={isRowSelectionDisabled}
            onToggleRow={onToggleRow}
            row={row}
            shouldHide={shouldHide}
          />
        ))}
      </BaseTable>
      {(hasNoRows || isHidingAllRows) && (
        <TextBlock
          primaryText={
            hasNoRows && !isPrefilteredAndNotEmpty
              ? emptyTablePrimaryText
              : resultsNotFoundPrimaryText
          }
          secondaryText={
            hasNoRows && !isPrefilteredAndNotEmpty
              ? emptyTableSecondaryText
              : resultsNotFoundSecondaryText
          }
        />
      )}
    </StyledTableContainer>
  );
}
